// UserMenu — MD3 Menu с user info + logout (R42 / U-13 Must из crm-ux-research §5.1).
// Кнопка: аватар (≥40px, MD3 touch-target) + на desktop имя; Tooltip «Аккаунт: <имя>».
// Меню: заголовок с displayName + email, пункты «Профиль» (R39b / U-10),
// «Администрирование» (V16 W11), «Команда» (R40 / U-11, admin-only) и «Выйти».
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChatIcon from '@mui/icons-material/Chat';
import { useAuth } from '@/auth/AuthContext';
import { t } from '@/i18n';

/** Инициалы из отображаемого имени (1-2 буквы). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function UserMenu(): JSX.Element {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  // R42: имя рядом с аватаром — только на desktop (на <lg текст скрыт, research §5.1).
  const showName = useMediaQuery(theme.breakpoints.up('lg'));

  const displayName = currentUser?.displayName ?? currentUser?.username ?? 'Гость';
  const email = currentUser?.email ?? '';

  // R40 / U-11: пункт «Команда» виден только администраторам (RoleGuard на /admin/team).
  // roles?. — защита от неполных моков/краевых состояний (любой пользователь без roles — не admin).
  const isAdmin = currentUser?.roles?.some((r) => r.code.toLowerCase() === 'admin') ?? false;

  return (
    <>
      <Tooltip title={t('topbar.account.tooltip', { name: displayName })}>
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Меню пользователя"
          data-testid="user-menu-button"
          color="inherit"
          sx={{ ml: 0.5, gap: 1 }}
        >
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 16 }}>
            {initials(displayName)}
          </Avatar>
          {showName ? (
            <Typography variant="body2" sx={{ display: { xs: 'none', lg: 'block' } }}>
              {displayName}
            </Typography>
          ) : null}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* R42: заголовок меню — имя + email (не голый аватар). */}
        <Box sx={{ px: 2, py: 1.5, minWidth: 220 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="user-menu-name">
            {displayName}
          </Typography>
          {email ? (
            <Typography variant="caption" color="text.secondary" component="div">
              {email}
            </Typography>
          ) : null}
        </Box>
        <Divider />
        {/* R39b / U-10: пункт «Профиль» — переход на страницу self-service редактирования. */}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate('/profile');
          }}
          data-testid="user-menu-profile"
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {t('menu.profile')}
        </MenuItem>
        {isAdmin ? (
          <>
            {/* V16 W11: пункт «Администрирование» — справочники, admin-only. */}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/admin/dictionaries');
              }}
              data-testid="user-menu-admin"
            >
              <ListItemIcon>
                <AdminPanelSettingsIcon fontSize="small" />
              </ListItemIcon>
              {t('menu.admin')}
            </MenuItem>
            {/* R40 / U-11: пункт «Команда» — admin-only. */}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/admin/team');
              }}
              data-testid="user-menu-team"
            >
              <ListItemIcon>
                <GroupIcon fontSize="small" />
              </ListItemIcon>
              {t('menu.team')}
            </MenuItem>
            {/* Tech-Story-jivo-admin-settings (R-FE-3): пункт «JivoChat» — admin-only. */}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/admin/jivo');
              }}
              data-testid="user-menu-jivo"
            >
              <ListItemIcon>
                <ChatIcon fontSize="small" />
              </ListItemIcon>
              {t('menu.jivo')}
            </MenuItem>
          </>
        ) : null}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            void logout();
          }}
          data-testid="user-menu-logout"
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('auth.logout')}
        </MenuItem>
      </Menu>
    </>
  );
}
